// ============================================
// Avatar Utility Functions
// ============================================

/**
 * Avatar color palette - 17 distinct colors
 * Used for generating consistent avatar colors based on user name
 */
const AVATAR_COLORS = [
  'bg-red-100 text-red-700',
  'bg-orange-100 text-orange-700',
  'bg-amber-100 text-amber-700',
  'bg-yellow-100 text-yellow-700',
  'bg-lime-100 text-lime-700',
  'bg-green-100 text-green-700',
  'bg-emerald-100 text-emerald-700',
  'bg-teal-100 text-teal-700',
  'bg-cyan-100 text-cyan-700',
  'bg-sky-100 text-sky-700',
  'bg-blue-100 text-blue-700',
  'bg-indigo-100 text-indigo-700',
  'bg-violet-100 text-violet-700',
  'bg-purple-100 text-purple-700',
  'bg-fuchsia-100 text-fuchsia-700',
  'bg-pink-100 text-pink-700',
  'bg-rose-100 text-rose-700',
];

/**
 * Generate initials from a name
 * @param name - Full name (e.g., "John Doe")
 * @returns Initials (e.g., "JD")
 */
export function getInitials(name: string): string {
  if (!name || typeof name !== 'string') {
    return '?';
  }

  const words = name.trim().split(/\s+/);

  if (words.length === 1) {
    // Single word: take first 2 characters
    return words[0].substring(0, 2).toUpperCase();
  }

  // Multiple words: take first letter of first and last word
  const firstInitial = words[0][0] || '';
  const lastInitial = words[words.length - 1][0] || '';

  return (firstInitial + lastInitial).toUpperCase();
}

/**
 * Generate a simple hash from a string
 * @param str - Input string
 * @returns Hash value (non-negative integer)
 */
function simpleHash(str: string): number {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = (hash << 5) - hash + char;
    hash = hash & hash; // Convert to 32-bit integer
  }
  return Math.abs(hash);
}

/**
 * Get a consistent avatar color class based on name
 * The color will always be the same for the same name
 * @param name - User's name
 * @returns Tailwind CSS classes for avatar background and text color
 */
export function getAvatarColor(name: string): string {
  if (!name || typeof name !== 'string') {
    return AVATAR_COLORS[0]; // Default to first color
  }

  const hash = simpleHash(name.toLowerCase());
  const index = hash % AVATAR_COLORS.length;

  return AVATAR_COLORS[index];
}
